import { defineStore } from "pinia";
import {
  Mate,
  MateModule,
  MateModuleTemplate,
  ModuleEntityType,
  ModuleQuestion,
  StoryBook,
  Worldview,
} from "@/types/entity";
import api from "@/modules/api";
import i18n from "@/locales";
import { useMate } from "@/stores/mate";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import {
  createOneMateModuleTemplate,
  deleteOneMateModuleTemplate,
  importMateModuleTemplate,
  readMateModuleTemplatePage,
  readWorldviewByUuid,
  updateOneMateModuleTemplate,
} from "@/apis/user";
import ModuleBaseImpl from "./module/moduleBaseImpl";
import { createModule } from "./module/moduleIndex";
import { useWorldview } from "./worldview";
// t('xxx')不具备响应性，因此需要放在具备响应性的pinia context中再用t渲染
// @see https://github.com/intlify/vue-i18n/issues/1396

export interface ModuleFieldItem extends ModuleQuestion {
  displayName: string;
  value: string;
}

export const useModule = defineStore("module", {
  state: () => {
    return {
      module: null as ModuleBaseImpl | null,
      extraEntities: {
        mate: [] as Mate[],
        storyBook: [] as StoryBook[],
      },
      mateModuleTemplates: [] as MateModuleTemplate[],
    };
  },
  actions: {
    /** 初始化module 并行获取问题和答案 */
    async initModule(uuid: string, entityType: ModuleEntityType) {
      let entity: Mate | Worldview;
      const mateStore = useMate();
      if (entityType === "mate") {
        entity = await mateStore.getOrFetchMate(uuid)!;
        await mateStore.changeCurrentMate(entity.id);
      } else {
        const res = await readWorldviewByUuid(uuid);
        entity = res.item;
      }
      // 2. 并行获取问题和答案
      const mateModules = await this.fetchMateModulePage(uuid, entityType);
      const questionMap = await this.loadAllMateModuleQuestions(
        mateModules.map((v) => v.id),
        uuid,
        entityType
      );

      this.module = createModule(entityType, entity!, mateModules, questionMap);
    },

    async loadAllMateModuleQuestions(
      mateModuleId: number[],
      uuid: string,
      entityType: ModuleEntityType
    ) {
      const questionMap: Record<number, ModuleQuestion[]> = {};

      for (let page = 1; ; page++) {
        const questionsRes = await api.readModuleQuestionPage({
          filter: {
            mateModuleId,
            entityUuid: uuid,
            entityType,
          },
          pagination: {
            perPage: 999,
          },
        });

        questionsRes.page.data.forEach((v) => {
          if (!questionMap[v.moduleId]) {
            questionMap[v.moduleId] = [];
          }
          questionMap[v.moduleId].push(v);
        });

        if (questionsRes.page.data.length < 999) break;
      }

      return questionMap;
    },

    // mateModule 管理
    async fetchMateModulePage(
      uuid: string,
      entityType: ModuleEntityType
    ): Promise<MateModule[]> {
      const { page } = await api.readMateModulePage({
        filter: { entityUuid: uuid, entityType },
      });
      if (page.additional.entities) {
        this.extraEntities = page.additional.entities;
      }
      return page.data;
    },
    async createMateModule(name: string) {
      const mateModuleRes = await api.createOneMateModule({
        entityId: this.module!.entity.id,
        entityType: this.module!.entityType!,
        name,
      });
      this.module?.addMateModule(mateModuleRes.item);
      return mateModuleRes.item.moduleId;
    },
    async updateMateModule(moduleId: number) {
      const mateModule = this.module!.moduleIdMateModuleMap[moduleId];
      const fields = this.module!.moduleFieldsMap[moduleId];
      const obj: Record<number, string> = {};
      fields.forEach((v) => {
        obj[v.id] = v.value;
      });
      if (!mateModule || Object.keys(obj).length === 0) return;
      await api.updateOneMateModule(mateModule.id!, { answerMap: obj });
    },

    // module 管理
    async updateModule(moduleId: number, name: string) {
      await api.updateOneModule(moduleId, name);
      this.module!.moduleIdMateModuleMap[moduleId].module.name = name;
    },
    async deleteModule(moduleId: number) {
      await api.deleteOneModule(
        moduleId,
        this.module!.entity.id,
        this.module!.entityType!
      );
      this.module!.mateModuleList = this.module!.mateModuleList.filter(
        (mateModule) => mateModule.moduleId !== moduleId
      );
      await this.module!.deleteMateModule(moduleId);
    },

    // question 管理
    async createQuestion(
      moduleId: number,
      text: string,
      fieldType?: number,
      entityType?: ModuleEntityType
    ) {
      const mateModule = this.module!.moduleIdMateModuleMap[moduleId];
      const res = await api.createOneModuleQuestion({
        mateModuleId: mateModule.id!,
        text,
        fieldType,
        entityType,
      });
      this.module!.moduleFieldsMap[moduleId].push(
        this.module!.convertQuestionToField(res.item)
      );
      return res.item.id;
    },
    async updateQuestion(moduleId: number, questionId: number, name: string) {
      await api.updateOneModuleQuestion(questionId, name);
      const q = this.module?.moduleFieldsMap[moduleId].find(
        (v) => v.id === questionId
      );
      if (q) q.displayName = name;
    },
    async deleteQuestion(moduleId: number, questionId: number) {
      showConfirmDialog({
        title: i18n.global.t("Message.deleteConfirm"),
        confirmButtonText: i18n.global.t("Buttons.sure"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      }).then(() => {
        const loading = showLoadingToast({
          duration: 0,
          message: i18n.global.t(`Message.deletingAttribute`),
        });
        api
          .deleteOneModuleQuestion(
            questionId,
            this.module!.entity.id,
            this.module!.entityType!
          )
          .then(() => {
            loading.close();
            const index = this.module!.moduleFieldsMap[moduleId].findIndex(
              (field) => field.id === questionId
            );
            showSuccessToast(i18n.global.t("Message.deleteSuccessful"));
            this.module!.moduleFieldsMap[moduleId].splice(index, 1);
            this.module!.deleteMateModuleQuestion(questionId);
          });
      });
    },

    // template 管理
    async createTemplate(moduleId: number, name: string) {
      const mateModule = this.module!.moduleIdMateModuleMap[moduleId];
      if (!mateModule) return;
      const res = await createOneMateModuleTemplate(mateModule.id!, name);
      if (this.mateModuleTemplates.length === 0) {
        await this.getTemplateList();
      } else {
        this.mateModuleTemplates.push(res.item);
      }
      return res.item;
    },

    async getTemplateList() {
      if (this.mateModuleTemplates.length > 0) return;
      const res = await readMateModuleTemplatePage({
        entityUuid: this.module!.entity.uuid!,
        entityType: this.module!.entityType!,
      });
      this.mateModuleTemplates = res.page.data;
    },

    async deleteTemplate(templateId: number) {
      await deleteOneMateModuleTemplate(templateId);
      this.mateModuleTemplates = this.mateModuleTemplates.filter(
        (item) => item.id !== templateId
      );
    },

    async updateTemplate(templateId: number, name: string) {
      await updateOneMateModuleTemplate(templateId, name);
      this.mateModuleTemplates = this.mateModuleTemplates.map((item) => {
        if (item.id === templateId) item.name = name;
        return item;
      });
    },

    async importTemplate(templateId: number) {
      await importMateModuleTemplate(
        this.module!.entity.id!,
        this.module!.entityType!,
        templateId
      );
      await this.initModule(
        this.module!.entity.uuid!,
        this.module!.entityType!
      );
    },

    isMyEntity(id?: number, entityType?: ModuleEntityType): boolean {
      if (!id || !entityType) return false;

      if (entityType === "mate") return useMate().isMyMateById(id);
      if (entityType === "worldview")
        return useWorldview().isMyWorldviewById(id);

      return false;
    },
  },
});
